import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Grid,
  useMediaQuery,
  Box,
  Typography,
  Button,
  FormControl,
  Snackbar,
  Alert,
  Slide,
  Link as MuiLink,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTheme } from "@mui/material/styles";
import {jwtDecode}  from "jwt-decode";

import SoftBox from "components/SoftBox";
import logo from "assets/images/Anser.png";
import cartoonImage from "assets/images/Login-amico.svg";
import axios from "axios";
import { FAST_API_BASE_URL } from "constants";

const Signup = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [role, setRole] = useState("");
  const [Id, setId] = useState("");

  const [loading, setLoading] = useState(false);
  const [decodingToken, setDecodingToken] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [mismatchError, setMismatchError] = useState("");
  const [emailAutoFilled, setEmailAutoFilled] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    confirmPassword: "",
  });

  const passwordRules = {
    letter: /[A-Za-z]/.test(formData.password),
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /[0-9]/.test(formData.password),
    specialChar: /[^A-Za-z0-9]/.test(formData.password),
    minLength: formData.password.length >= 8,
  };

  const allRulesPassed = Object.values(passwordRules).every(Boolean);
  const decodeTokenAndAutoFill = () => {
    const token = queryParams.get("secret") || sessionStorage.getItem("authToken");
    
    if (!token) {
      console.log("No JWT token found for auto-fill");
      return false;
    }

    setDecodingToken(true);
    try {
      const decodedToken = jwtDecode(token);
      console.log("Decoded JWT token:", decodedToken);
      const email = decodedToken.email || decodedToken.user_email || decodedToken.sub || "";
      const userName = decodedToken.user_name || decodedToken.username || decodedToken.name || 
                      decodedToken.preferred_username || decodedToken.given_name || "";

      if (email || userName) {
        setFormData(prev => ({
          ...prev,
          email: email || prev.email,
          name: userName || prev.name
        }));
        if (email) {
          setEmailAutoFilled(true);
        }
        
        console.log("Auto-filled from JWT token:", {
          email,
          userName,
          fullDecodedToken: decodedToken
        });

        if (email || userName) {
          setSnackbarMessage("Your information has been auto-filled!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }
        
        return true;
      }
    } catch (error) {
      console.error("Failed to decode JWT token:", error);
      // setSnackbarMessage("Unable to auto-fill information. Please enter manually.");
      // setSnackbarSeverity("warning");
      // setSnackbarOpen(true);
    } finally {
      setDecodingToken(false);
    }
    
    return false;
  };

  const findAndDecodeToken = () => {
    const possibleTokenSources = [
      queryParams.get("secret"),
    ];

    const token = possibleTokenSources.find(source => source && source.length > 10);

    if (token) {
      try {
        const decoded = jwtDecode(token);
        const email = decoded.email || decoded.user_email || decoded.sub || "";
        const userName = decoded.user_name || decoded.username || decoded.name || 
                        decoded.preferred_username || decoded.given_name || "";

        if (email || userName) {
          setFormData(prev => ({
            ...prev,
            email: email || prev.email,
            name: userName || prev.name
          }));
          if (email) {
            setEmailAutoFilled(true);
          }
          
          return true;
        }
      } catch (error) {
        console.error("Token decoding failed:", error);
      }
    }
    return false;
  };

  useEffect(() => {
    const domainId = queryParams.get("domain_id");
    const roleParam = queryParams.get("role");
    const secret = queryParams.get("secret");
    const Id = queryParams.get("id");
    const token = queryParams.get("token");
    
    if (Id) {
      setId(Id);
      sessionStorage.setItem("id", Id);
    }

    if (roleParam) {
      setRole(roleParam);
      sessionStorage.setItem("role", roleParam);
    }

    if (token) {
      sessionStorage.setItem("authToken", token);
    }

    if (secret) {
      sessionStorage.setItem("secret", secret);
      sessionStorage.removeItem("domainId");
    } else if (domainId) {
      sessionStorage.setItem("domainId", domainId);
      sessionStorage.removeItem("secret");
    } else {
      const authToken = sessionStorage.getItem("authToken");
      sessionStorage.clear();
      if (authToken) {
        sessionStorage.setItem("authToken", authToken);
      }
    }

    const autoFilled = findAndDecodeToken();
    if (!autoFilled) {
      decodeTokenAndAutoFill();
    }
  }, [location.search]);

  const getHeaders = () => {
    const domainId = sessionStorage.getItem("domainId");
    const secret = sessionStorage.getItem("secret");
    const authToken = sessionStorage.getItem("authToken");

    if (authToken) {
      return { "Authorization": `Bearer ${authToken}` };
    }
    if (secret) {
      return { "SECRET-TOKEN": secret };
    }
    if (domainId) {
      return { "DOMAIN-TOKEN": domainId };
    }
    return {};
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (e.target.name === "confirmPassword") {
      setMismatchError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = getHeaders();

    if (!formData.email || !formData.password || !formData.name || !formData.confirmPassword) {
      alert("Please fill in all required fields");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setMismatchError("Passwords do not match!");
      return;
    }

    if (!allRulesPassed) {
      alert("Password does not meet the required criteria");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        password: formData.password,
        email: formData.email,
        user_name: formData.name,
        role: role || "",
        id: Id || "",
      };

      const response = await axios.post(
        `${FAST_API_BASE_URL}/api/save-password`,
        payload,
        {
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setSnackbarMessage("Account created successfully! Please wait...");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setSuccess(true);
        sessionStorage.setItem("custom", true);
        sessionStorage.setItem("customEmail", formData.email);
        sessionStorage.setItem("name", formData.name);

        sessionStorage.removeItem("authToken");

        setTimeout(() => {
          if (sessionStorage.getItem("role") === "read only") {
            navigate("/dashboard");
          } else if (sessionStorage.getItem("is_super_admin") === "true") {
            navigate("/customers");
          } else {
            navigate("/settings");
          }
        }, 1000);
      }
    } catch (err) {
      console.error("Signup failed:", err);
      setSnackbarMessage("Failed to create account! Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const renderRule = (label, passed) => ( 
    <Box display="flex" alignItems="center" mb={1}> 
      {passed ? ( 
        <CheckCircleIcon sx={{ mr: 1, color: "#219297", fontSize: "12px" }} />
      ) : ( 
        <CancelIcon color="error" fontSize="small" sx={{ mr: 1, fontSize: "12px" }} /> 
      )} 
      <Typography variant="body2" color={passed ? "#219297" : "text.secondary"} sx={{ fontSize: "12px" }}> 
        {label} 
      </Typography> 
    </Box>
  );

  const navigateToLogin = () => {
    navigate("/login");
  };

  return (
    <>
      <SoftBox px={2} py={1} width="100%" bgColor="white" shadow="md">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <img src={logo} alt="Ansera Logo" style={{ height: "3rem" }} />
        </Box>
      </SoftBox>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="top" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#fff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#008080"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : snackbarSeverity === "warning"
                ? "#ed6c02"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SoftBox
        sx={{
          minHeight: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgColor: "#f0f4f7",
          px: 2,
          py: { xs: 2, sm: 2 },
          overflowY: "auto",
        }}
      >
        <Grid container spacing={4} alignItems="center" justifyContent="center" maxWidth="xl">
          {!isMobile && (
            <Grid item md={6} lg={6} xl={6} sx={{ display: { xs: "none", md: "block" } }}>
              <Box
                component="img"
                src={cartoonImage}
                alt="Signup Illustration"
                sx={{
                  width: "100%",
                  maxWidth: { md: "350px", lg: "450px", xl: "550px" },
                  mx: "auto",
                }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={10} md={6} lg={5} xl={4}>
            <Card
              sx={{
                p: { xs: 3, sm: 4 },
                maxWidth: 480,
                mx: "auto",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                borderRadius: 4,
              }}
            >
              <Grid container direction="column" spacing={3} alignItems="center">
                <Grid item>
                  <Typography
                    fontWeight="bold"
                    align="center"
                    sx={{
                      fontSize: "clamp(1.4rem, 2.5vw, 2rem)",
                      color: "text.primary",
                    }}
                  >
                    Sign Up
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      fontSize: "clamp(0.8rem, 1.2vw, 1rem)",
                      color: "text.secondary",
                    }}
                  >
                    Create your Ansera Search account
                  </Typography>
                  {decodingToken && (
                    <Typography
                      variant="body2"
                      align="center"
                      sx={{
                        fontSize: "0.8rem",
                        color: "text.secondary",
                        fontStyle: "italic",
                        mt: 1
                      }}
                    >
                      Auto-filling your information...
                    </Typography>
                  )}
                </Grid>

                <Grid item sx={{ width: "100%" }}>
                  <form onSubmit={handleSubmit}>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <label
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#555",
                          marginBottom: "6px",
                        }}
                      >
                        Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Enter your name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={decodingToken}
                        style={{
                          width: "100%",
                          padding: "12px 14px",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          fontSize: "15px",
                          backgroundColor: decodingToken ? "#f5f5f5" : "white",
                        }}
                      />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <label
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#555",
                          marginBottom: "6px",
                        }}
                      >
                        Email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={emailAutoFilled}
                        style={{
                          width: "100%",
                          padding: "12px 14px",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          fontSize: "15px",
                          backgroundColor: emailAutoFilled ? "#f5f5f5" : "white",
                          cursor: emailAutoFilled ? "not-allowed" : "text",
                        }}
                      />
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 1, position: "relative" }}>
                      <label
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#555",
                          marginBottom: "6px",
                        }}
                      >
                        Password
                      </label>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter password"
                        required
                        style={{
                          width: "100%",
                          padding: "12px 40px 12px 14px",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          fontSize: "15px",
                        }}
                      />
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "38px",
                          cursor: "pointer",
                          fontSize: "18px",
                          color: "#888",
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </span>
                    </FormControl>

                    {formData.password && (
                      <Box mt={2}>
                        {renderRule("At least 8 characters", passwordRules.minLength)}
                        {renderRule("Includes a number", passwordRules.number)}
                        {renderRule("Includes a special character", passwordRules.specialChar)}
                        {renderRule("Includes Uppercase[A-Z]", passwordRules.uppercase)}
                        {renderRule("Includes Lowercase[a-z]", passwordRules.lowercase)}
                      </Box>
                    )}

                    <FormControl fullWidth sx={{ mb: 3, position: "relative" }}>
                      <label
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#555",
                          marginBottom: "6px",
                        }}
                      >
                        Confirm Password
                      </label>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Re-enter password"
                        required
                        style={{
                          width: "100%",
                          padding: "12px 40px 12px 14px",
                          borderRadius: "8px",
                          border: "1px solid #ccc",
                          fontSize: "15px",
                        }}
                      />
                      <span
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "38px",
                          cursor: "pointer",
                          fontSize: "18px",
                          color: "#888",
                        }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </span>
                      {mismatchError && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                          {mismatchError}
                        </Typography>
                      )}
                    </FormControl>

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={loading || !allRulesPassed || decodingToken}
                      sx={{
                        py: { xs: 1, sm: 1.2 },
                        borderRadius: "8px",
                        backgroundColor: (!allRulesPassed || decodingToken) ? "#ccc" : "#219297",
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "clamp(0.85rem, 1vw, 1rem)",
                      }}
                    >
                      {loading ? "Creating Account..." : 
                       decodingToken ? "Loading..." : "Sign Up"}
                    </Button>
                  </form>
                </Grid>

                <Grid item>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{
                      mt: 0,
                      fontSize: "clamp(0.8rem, 1vw, 0.95rem)",
                      color: "text.secondary",
                    }}
                  >
                    Already have an account?{" "}
                    <span
                      style={{
                        color: "#008080",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      onClick={navigateToLogin}
                    >
                      Sign in
                    </span>
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </>
  );
};

export default Signup;